/** Upload de vídeo — contorna limite ~100MB do proxy Cloudflare Free via chunks. */

const CHUNK_BYTES = 16 * 1024 * 1024; // 16MB (margem sob ~100MB da CF)
/** Abaixo disso tenta POST único; acima (ou se 413) usa chunks. CF Free corta ~100MB. */
const DIRECT_MAX_BYTES = 50 * 1024 * 1024;

export interface UploadVideoInput {
  file: File;
  customName: string;
  codec: 'h264' | 'h265';
  sizeMode: 'auto' | 'target';
  targetSizeMB?: number;
  width?: number;
  height?: number;
  normalizeAudio?: boolean;
}

export interface UploadProgressInfo {
  loaded: number;
  total: number;
  phase: 'uploading' | 'finalizing';
}

export type UploadProgressCallback = (info: UploadProgressInfo) => void;

export interface UploadVideoResult {
  jobId: string;
  message?: string;
}

async function readApiError(response: Response): Promise<string> {
  if (response.status === 413) {
    return 'Arquivo rejeitado (413 Content Too Large). O proxy limita ~100MB por requisição — use o envio em partes.';
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      const data = (await response.json()) as { error?: string };
      return data.error || `Erro HTTP ${response.status}`;
    } catch {
      return `Erro HTTP ${response.status}`;
    }
  }

  const text = await response.text().catch(() => '');
  if (
    response.status === 413 ||
    /413|too large|content too large|entity too large/i.test(text)
  ) {
    return 'Arquivo rejeitado (413). Tamanho máximo por requisição no proxy é ~100MB.';
  }

  return text.trim().slice(0, 180) || `Erro HTTP ${response.status}`;
}

function appendOptionalSettings(formData: FormData, input: UploadVideoInput): void {
  if (input.sizeMode === 'target' && input.targetSizeMB != null) {
    formData.append('targetSizeMB', String(input.targetSizeMB));
  }
  if (input.width != null) formData.append('width', String(input.width));
  if (input.height != null) formData.append('height', String(input.height));
  if (input.normalizeAudio) formData.append('normalizeAudio', 'true');
}

function settingsPayload(input: UploadVideoInput): Record<string, unknown> {
  return {
    codec: input.codec,
    sizeMode: input.sizeMode,
    targetSizeMB: input.sizeMode === 'target' ? input.targetSizeMB : undefined,
    width: input.width,
    height: input.height,
    normalizeAudio: input.normalizeAudio || undefined,
  };
}

/** POST com XMLHttpRequest para progresso nativo de bytes. */
function xhrPostFormData(
  url: string,
  formData: FormData,
  onProgress?: UploadProgressCallback,
  totalHint?: number
): Promise<{ status: number; body: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.withCredentials = true;

    xhr.upload.onprogress = (e) => {
      if (!onProgress) return;
      const total = e.lengthComputable ? e.total : (totalHint ?? 0);
      onProgress({
        loaded: e.loaded,
        total: total || e.loaded,
        phase: 'uploading',
      });
    };

    xhr.onload = () => {
      resolve({ status: xhr.status, body: xhr.responseText });
    };

    xhr.onerror = () => {
      reject(new Error('Falha de rede no upload'));
    };

    xhr.onabort = () => {
      reject(new Error('Upload cancelado'));
    };

    xhr.send(formData);
  });
}

function parseJsonBody(body: string): {
  success?: boolean;
  data?: { jobId?: string; message?: string; uploadId?: string };
  error?: string;
} {
  try {
    return JSON.parse(body) as {
      success?: boolean;
      data?: { jobId?: string; message?: string; uploadId?: string };
      error?: string;
    };
  } catch {
    return {};
  }
}

async function errorFromXhr(status: number, body: string): Promise<string> {
  if (status === 413) {
    return 'Arquivo rejeitado (413 Content Too Large). O proxy limita ~100MB por requisição — use o envio em partes.';
  }
  const json = parseJsonBody(body);
  if (json.error) return json.error;
  if (/413|too large|content too large|entity too large/i.test(body)) {
    return 'Arquivo rejeitado (413). Tamanho máximo por requisição no proxy é ~100MB.';
  }
  return body.trim().slice(0, 180) || `Erro HTTP ${status}`;
}

async function uploadDirect(
  input: UploadVideoInput,
  onProgress?: UploadProgressCallback
): Promise<UploadVideoResult> {
  const formData = new FormData();
  formData.append('video', input.file);
  formData.append('codec', input.codec);
  formData.append('sizeMode', input.sizeMode);
  formData.append('customName', input.customName);
  appendOptionalSettings(formData, input);

  const { status, body } = await xhrPostFormData(
    '/api/upload',
    formData,
    onProgress,
    input.file.size
  );

  if (status < 200 || status >= 300) {
    throw new Error(await errorFromXhr(status, body));
  }

  const result = parseJsonBody(body);
  if (!result.success || !result.data?.jobId) {
    throw new Error(result.error || 'Resposta de upload inválida');
  }

  onProgress?.({ loaded: input.file.size, total: input.file.size, phase: 'finalizing' });
  return { jobId: result.data.jobId, message: result.data.message };
}

async function uploadChunked(
  input: UploadVideoInput,
  onProgress?: UploadProgressCallback
): Promise<UploadVideoResult> {
  const totalChunks = Math.max(1, Math.ceil(input.file.size / CHUNK_BYTES));
  const totalSize = input.file.size;

  const initRes = await fetch('/api/upload/init', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      originalName: input.file.name,
      customName: input.customName,
      ...settingsPayload(input),
      totalSize,
      totalChunks,
    }),
  });

  if (!initRes.ok) {
    throw new Error(await readApiError(initRes));
  }

  const initJson = (await initRes.json()) as {
    success?: boolean;
    data?: { uploadId?: string };
    error?: string;
  };

  const uploadId = initJson.data?.uploadId;
  if (!initJson.success || !uploadId) {
    throw new Error(initJson.error || 'Falha ao iniciar upload em partes');
  }

  let bytesSent = 0;

  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_BYTES;
    const end = Math.min(start + CHUNK_BYTES, input.file.size);
    const blob = input.file.slice(start, end);
    const chunkSize = end - start;

    const formData = new FormData();
    formData.append('chunk', blob, `chunk-${i}`);
    formData.append('uploadId', uploadId);
    formData.append('chunkIndex', String(i));

    const baseLoaded = bytesSent;
    const { status, body } = await xhrPostFormData(
      '/api/upload/chunk',
      formData,
      (info) => {
        onProgress?.({
          loaded: baseLoaded + info.loaded,
          total: totalSize,
          phase: 'uploading',
        });
      },
      chunkSize
    );

    if (status < 200 || status >= 300) {
      throw new Error(await errorFromXhr(status, body));
    }

    bytesSent += chunkSize;
    onProgress?.({ loaded: bytesSent, total: totalSize, phase: 'uploading' });
  }

  onProgress?.({ loaded: totalSize, total: totalSize, phase: 'finalizing' });

  const completeRes = await fetch('/api/upload/complete', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ uploadId }),
  });

  if (!completeRes.ok) {
    throw new Error(await readApiError(completeRes));
  }

  const completeJson = (await completeRes.json()) as {
    success?: boolean;
    data?: { jobId?: string; message?: string };
    error?: string;
  };

  if (!completeJson.success || !completeJson.data?.jobId) {
    throw new Error(completeJson.error || 'Falha ao finalizar upload');
  }

  return {
    jobId: completeJson.data.jobId,
    message: completeJson.data.message,
  };
}

/** Envia vídeo: POST único se pequeno; chunks se grande ou se o proxy devolver 413. */
export async function uploadVideoFile(
  input: UploadVideoInput,
  onProgress?: UploadProgressCallback
): Promise<UploadVideoResult> {
  if (input.file.size <= DIRECT_MAX_BYTES) {
    try {
      return await uploadDirect(input, onProgress);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (!/413|too large|content too large|entity too large/i.test(msg)) {
        throw err;
      }
      // Fallback: proxy cortou o body — envia em partes
    }
  }

  return uploadChunked(input, onProgress);
}

/** Formata bytes para exibição (ex.: 12.4 MB). */
export function formatUploadBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
